import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../lib/api';
import { formatDate, generateReferralLink } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Users, Copy, UserPlus, TrendingUp } from 'lucide-react';

const Referrals = () => {
  const { user } = useAuth();
  const [downline, setDownline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDownline(); }, [user]);

  const loadDownline = async () => {
    if (!user?.id) return;
    try { const res = await userAPI.getDownline(user.id); setDownline(res.data); }
    catch (error) { console.error('Error loading downline:', error); }
    finally { setLoading(false); }
  };

  const copyReferralLink = () => {
    const link = generateReferralLink(user?.referral_code);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div></div>);
  }

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Referrals</h1>
        <p className="text-gray-500 mt-1">Invite friends and earn commission</p>
      </div>

      <GlassCard className="border-l-4 border-l-[#4E50FF]">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-5 h-5 text-[#4E50FF]" />
              <h3 className="font-semibold text-gray-900">Share Your Referral Link</h3>
            </div>
            <p className="text-sm text-gray-500 max-w-md">
              When your referrals purchase a property, you'll earn commission as an introducer.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="px-6 py-3 bg-[#4E50FF]/5 rounded-lg border border-[#4E50FF]/20">
              <span className="text-xl font-mono font-bold text-[#4E50FF]">{user?.referral_code}</span>
            </div>
            <Button onClick={copyReferralLink} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white">
              <Copy className="w-4 h-4 mr-2" />Copy Referral Link
            </Button>
          </div>
        </div>
      </GlassCard>

      <div className="grid md:grid-cols-3 gap-6">
        <GlassCard className="text-center">
          <Users className="w-8 h-8 text-[#4E50FF] mx-auto mb-2" />
          <p className="text-3xl font-bold text-gray-900 font-mono">{downline.length}</p>
          <p className="text-sm text-gray-500">Total Referrals</p>
        </GlassCard>
        <GlassCard className="text-center">
          <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-gray-900 font-mono">{downline.filter(d => d.kyc_status === 'approved').length}</p>
          <p className="text-sm text-gray-500">Verified Referrals</p>
        </GlassCard>
        <GlassCard className="text-center">
          <UserPlus className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-gray-900 font-mono">{downline.filter(d => d.role === 'agent').length}</p>
          <p className="text-sm text-gray-500">Agent Referrals</p>
        </GlassCard>
      </div>

      <GlassCard>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Referrals</h2>
        {downline.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100">
                <TableHead className="text-gray-500 font-semibold">Name</TableHead>
                <TableHead className="text-gray-500 font-semibold">Email</TableHead>
                <TableHead className="text-gray-500 font-semibold">Role</TableHead>
                <TableHead className="text-gray-500 font-semibold">KYC Status</TableHead>
                <TableHead className="text-gray-500 font-semibold">Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {downline.map((member) => (
                <TableRow key={member.id} className="border-gray-50">
                  <TableCell className="text-gray-900 font-medium">{member.full_name}</TableCell>
                  <TableCell className="text-gray-500">{member.email}</TableCell>
                  <TableCell className="text-gray-700 capitalize">{member.role}</TableCell>
                  <TableCell><StatusBadge status={member.kyc_status} /></TableCell>
                  <TableCell className="text-gray-500">{formatDate(member.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Referrals Yet</h3>
            <p className="text-gray-400 mb-4">Share your referral link to start earning</p>
            <Button onClick={copyReferralLink} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white">
              <Copy className="w-4 h-4 mr-2" />Copy Referral Link
            </Button>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default Referrals;
